import { authService } from './auth';
import { i18nService } from './i18n';
import { trimScenarioRecentUsage, type ScenarioRecentUsage } from './recentScenarioModel';

export interface ScenarioSkill {
  skill_id: number;
  skill_name: string;
  display_name: string;
  /** 技能版本（老版本 platform 不下发） */
  version?: string;
  /** 平台侧上架状态：1=启用；undefined 按启用处理（老版本 platform 不下发） */
  status?: number;
}

export interface WorkflowStep {
  title: string;
  description: string;
  // 历史字段，可选：后端当前不返回 step 号，由前端按数组顺序生成
  step?: number;
}

export interface TriggerPrompt {
  /** 按钮展示文字（短标签） */
  label: string;
  /** 点击后回填输入框的完整提示词 */
  prompt: string;
  icon?: string;
  category?: string;
}

export interface MyScenario {
  id: number;
  name: string;
  description: string;
  group_id: number;
  group_name: string;
  icon: string;
  tags: string;
  system_prompt: string;
  knowledge: string;
  status: number;
  is_hot: boolean;
  is_recommended: boolean;
  usage_count: number;
  sort_order: number;
  skills: ScenarioSkill[];
  // 场景应用封装新增字段（CE 管理端后端已返回，前端扩展）
  expert_name?: string;
  greeting?: string;
  workflow_steps?: string; // JSON string of WorkflowStep[]
  trigger_prompts?: string; // JSON string of TriggerPrompt[]
  visibility?: string;
  recommended_model?: string;
  allow_model_switch?: boolean;
}

export interface ScenarioGroup {
  group_id: number;
  group_name: string;
  scenarios: MyScenario[];
}

export interface ScenarioGroupInfo {
  id: number;
  name: string;
  icon: string;
  description: string;
  tags: string;
  sort_order: number;
  status: number;
}

class ScenarioService {
  private scenarios: MyScenario[] = [];
  private allGroups: ScenarioGroupInfo[] = [];
  private listeners = new Set<() => void>();
  private _loading = false;
  private _loadFailed = false;
  private requestSequence = 0;

  /** 单例只订阅一次；身份改变先清空列表和请求代次，禁止旧账号响应重新填充页面。 */
  constructor() {
    if (typeof window !== 'undefined') window.addEventListener('auth:identityChanging', () => {
      this.requestSequence++; this.scenarios = []; this.allGroups = [];
      this._loading = false; this._authFailed = false; this._loadFailed = false; this.notify();
    });
  }


  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    this.listeners.forEach(l => l());
  }

  get isLoading(): boolean {
    return this._loading;
  }

  /** 最近一次目录读取失败；区别于有效空列表和需要重新登录的凭据失效。 */
  get loadFailed(): boolean {
    return this._loadFailed;
  }

  getScenarios(): MyScenario[] {
    return this.scenarios;
  }

  getGroups(): ScenarioGroupInfo[] {
    return this.allGroups;
  }

  getGroupedScenarios(): ScenarioGroup[] {
    const groupMap = new Map<number, ScenarioGroup>();
    for (const s of this.scenarios) {
      if (!groupMap.has(s.group_id)) {
        groupMap.set(s.group_id, { group_id: s.group_id, group_name: s.group_name, scenarios: [] });
      }
      groupMap.get(s.group_id)!.scenarios.push(s);
    }
    return Array.from(groupMap.values());
  }

  /** 上次加载是否因平台登录态失效而失败（区分"真没有场景"与"凭证过期"）。 */
  private _authFailed = false;

  get authFailed(): boolean {
    return this._authFailed;
  }

  /** 重新读取平台授权目录并核对技能关联；身份变化、撤权或读取失败均拒绝使用旧快照。 */
  async assertScenarioAccess(scenario: MyScenario): Promise<void> {
    const identity = authService.getSessionIdentity();
    const base = authService.getBaseUrl();
    const current = (await this.loadScenarios()).find((item) => item.id === scenario.id);
    if (identity !== authService.getSessionIdentity() || base !== authService.getBaseUrl()) throw new Error('验证期间账号或平台已切换，请重新进入场景');
    if (!current) throw new Error('场景权限已变更或暂时无法验证，请刷新场景列表后重试');
    const links = (item: MyScenario) => JSON.stringify((item.skills || []).map((skill) =>
      [skill.skill_id, skill.skill_name, skill.version, skill.status]).sort((a, b) => Number(a[0]) - Number(b[0])));
    if (links(current) !== links(scenario)) throw new Error('场景关联技能已变更，请刷新场景列表后重试');
  }

  async loadScenarios(): Promise<MyScenario[]> {
    const identity = authService.getSessionIdentity();
    const baseUrl = authService.getBaseUrl();
    const request = ++this.requestSequence;
    const current = () => request === this.requestSequence && identity === authService.getSessionIdentity() && baseUrl === authService.getBaseUrl();
    if (!identity) {
      this.scenarios = []; this.allGroups = []; this._loading = false; this._authFailed = false; this._loadFailed = false;
      this.notify();
      return [];
    }

    this._loading = true;
    this._loadFailed = false;
    this._authFailed = false;
    this.notify();

    try {
      // 经 web 服务端代理调平台（服务端持用户平台凭证）——浏览器侧的 web 会话
      // JWT 平台不认，直连必 401（历史 bug：401 被吞成"暂无可用场景"）。
      const language = i18nService.getLanguage();
      const result = await (window.electron.cowork as unknown as {
        getScenarios?: (lang: string) => Promise<{ success: boolean; authFailed?: boolean; scenarios?: MyScenario[]; groups?: ScenarioGroupInfo[] }>;
      }).getScenarios?.(language);

      if (!current()) return [];
      this._authFailed = Boolean(result?.authFailed);
      const valid = result?.success === true && Array.isArray(result.scenarios);
      this._loadFailed = !this._authFailed && !valid;
      this.scenarios = valid && !this._authFailed ? result.scenarios! : [];
      this.allGroups = valid && !this._authFailed && Array.isArray(result.groups) ? result.groups : [];
    } catch (error) {
      if (!current()) return [];
      console.error('[scenario] Failed to load scenarios:', error);
      this._loadFailed = true;
      this.scenarios = []; this.allGroups = [];
    } finally {
      if (current()) { this._loading = false; this.notify(); }
    }

    return this.scenarios;
  }

  async loadScenarioGroups(): Promise<void> { /* loaded in loadScenarios */ }

  // ── 场景-会话关联持久化（localStorage） ──
  // 让会话关闭应用后重新打开、或从侧边栏切回时，仍能恢复场景上下文（greeting/workflow/trigger prompts）。

  /** 旧全局快照没有账号归属，不能自动迁给当前登录者；可从服务端会话标识恢复。 */
  private storageKey(kind: 'sessions' | 'recent-usage' = 'sessions'): string | null {
    const auth = authService.getAuthState();
    return auth ? `boclaw:scenario-${kind}:${JSON.stringify([authService.getBaseUrl(), String(auth.user.id)])}` : null;
  }

  /** 将场景快照关联到某个 session */
  saveSessionScenario(sessionId: string, scenario: MyScenario): void {
    if (!sessionId || sessionId.startsWith('temp-')) return;
    try {
      const map = this.getSessionScenarioMap();
      map[sessionId] = scenario;
      // 最多保留 50 条，淘汰最早写入的
      const keys = Object.keys(map);
      if (keys.length > 50) {
        for (const k of keys.slice(0, keys.length - 50)) {
          delete map[k];
        }
      }
      const key = this.storageKey();
      if (key) localStorage.setItem(key, JSON.stringify(map));
    } catch (e) {
      console.error('[scenario] Failed to save session scenario:', e);
    }
  }

  /** 获取某个 session 关联的场景（快照） */
  getSessionScenario(sessionId: string): MyScenario | null {
    if (!sessionId) return null;
    const map = this.getSessionScenarioMap();
    return map[sessionId] || null;
  }

  /** 清除某个 session 的场景关联 */
  removeSessionScenario(sessionId: string): void {
    if (!sessionId) return;
    try {
      const map = this.getSessionScenarioMap();
      if (!(sessionId in map)) return;
      delete map[sessionId];
      const key = this.storageKey();
      if (key) localStorage.setItem(key, JSON.stringify(map));
    } catch {}
  }

  private getSessionScenarioMap(): Record<string, MyScenario> {
    try {
      const key = this.storageKey();
      const raw = key ? localStorage.getItem(key) : null;
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return Object.create(null);
      return Object.assign(Object.create(null), parsed);
    } catch {
      return Object.create(null);
    }
  }

  /** 最近使用按当前平台和账号读取，历史无归属数据不自动认领。 */
  getRecentUsage(): ScenarioRecentUsage[] {
    try {
      const key = this.storageKey('recent-usage');
      return trimScenarioRecentUsage(key ? JSON.parse(localStorage.getItem(key) || '[]') : []);
    } catch { return []; }
  }

  /** 只在明确使用时更新本机历史；存储不可用不能阻断用户继续任务。 */
  markScenarioUsed(scenarioId: number): void {
    if (!Number.isSafeInteger(scenarioId) || scenarioId <= 0) return;
    const key = this.storageKey('recent-usage');
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(trimScenarioRecentUsage([
        { scenarioId, lastUsedAt: Date.now() }, ...this.getRecentUsage(),
      ])));
      this.notify();
    } catch { /* 无痕模式或配额不足只影响推荐，不影响会话。 */ }
  }

  /** 实际发起场景任务时记录使用；平台统计失败不阻断本机任务。 */
  async recordUsage(scenarioId: number): Promise<void> {
    const auth = authService.getAuthState();
    if (!auth) return;
    this.markScenarioUsed(scenarioId);

    try {
      await (window.electron.cowork as unknown as {
        recordScenarioUsage?: (scenarioId: number) => Promise<{ success: boolean }>;
      }).recordScenarioUsage?.(scenarioId);
    } catch (error) {
      console.error('[scenario] Failed to record usage:', error);
    }
  }
}

export const scenarioService = new ScenarioService();
